import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Checkbox, useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { CACHE_KEYS, useUpdateAccount } from "@rentablo/api/apiv1";
import { ExtendedAccount } from "@features/depots/types";
import { buildKpmgReportingMetadataUpdate } from "@helpers";

interface Props {
  account: ExtendedAccount;
}

export const KpmgReportingCheckbox = ({ account }: Props) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();
  const { id, name, type, metadata, isKpmgReportingEnabled } = account;
  const [isEnabled, setIsEnabled] = useState(isKpmgReportingEnabled ?? false);

  const { mutateAsync: updateAccountMutate, isPending: isUpdatingReporting } =
    useUpdateAccount({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_ACCOUNTS],
        });
      },
    });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync from prop
    setIsEnabled(isKpmgReportingEnabled ?? false);
  }, [isKpmgReportingEnabled]);

  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    event.stopPropagation();
    if (!type || isUpdatingReporting) {
      return;
    }
    setIsEnabled(checked);
    try {
      await updateAccountMutate({
        id,
        createOrUpdateAccountParams: {
          type,
          name,
          metadata: buildKpmgReportingMetadataUpdate(metadata, checked),
        },
      });
    } catch {
      setIsEnabled(!checked);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minWidth={isSm ? "38px" : "68px"}
    >
      {type === "01_depot" && (
        <Checkbox
          checked={isEnabled}
          onChange={handleChange}
          onClick={(event) => event.stopPropagation()}
          inputProps={{ "aria-label": "Select account" }}
          sx={{ pl: { xs: 0, sm: "9px" } }}
        />
      )}
    </Box>
  );
};
